// src/pages/Vendedor.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getProjectById, getProjects } from '../services/ProjectServices';
import { getMediaUrl } from '../services/CoreServices';
import ProjectFilters from '../components/ProjectFilters';
import ProjectQuotes from '../components/ProjectQuotes';
import ProjectUnits from '../components/ProjectUnits';
import * as crmServices from '../services/CRMServices';

import './Salesperson.css';

function Salesperson() {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [projectList, setProjectList] = useState([]);
  const [rotatorUrl, setRotatorUrl] = useState(null);
  const [unit, setUnit] = useState(null);
  const [units, setUnits] = useState([]);
  const unitsRef = useRef(units);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projects = await getProjects();
        setProjectList(projects);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    unitsRef.current = units;
  }, [units]);

  const normalizeTypology = React.useCallback((rawTypology) => {
    return rawTypology
      .replace(/^tipo-/, '')
      .toUpperCase();
  }, []);

  const handleSelectPolygon = React.useCallback((elementName) => {
    const newUnit = unitsRef.current.find((u) => {
      const unitSlugParts = u.slug?.split('-') || [];
      const typologySlugParts = u.typologySlug?.split('-') || [];
      let formattedName = '?';

      if (unitSlugParts.length >= 5 && typologySlugParts.length >= 4) {
        const block = unitSlugParts[2];
        const stage = unitSlugParts[3];
        const numero = unitSlugParts[4];
        const tipologyRaw = typologySlugParts[3];
        const tipology = normalizeTypology(tipologyRaw);
        const tower = String.fromCharCode(64 + parseInt(stage));
        formattedName = `${block}-${tower}-${tipology}-${numero}`;
      } else if (unitSlugParts.length === 4) {
        const typologyLetter = u.typologySlug.slice(-1).toUpperCase();
        formattedName = `${unitSlugParts[1]}-${unitSlugParts[2].toUpperCase()}-${typologyLetter}-${unitSlugParts[3]}`;
      }

      return formattedName === elementName;
    });

    if (newUnit) {
      setUnit(newUnit);
    } else {
      console.error("Unit not found for polygon:", elementName);
    }
  }, [normalizeTypology]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'selectPolygon') {
        handleSelectPolygon(event.data.elementName);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleSelectPolygon]);

  const handleSelectProject = async (project) => {
    setLoading(true);
    try {
      setError(null);
      const p = await getProjectById(project.id);
      setProject(p);

      const allUnits = [];
      if (p.stages && p.stages.length > 0) {
        p.stages.forEach(stage => {
          if (stage.blocks && stage.blocks.length > 0) {
            stage.blocks.forEach(block => {
              if (block.units && block.units.length > 0) {
                allUnits.push(...block.units);
              }
            });
          }
        });
      }

      setUnits(allUnits);

      if (p.orbitViews) {
        const baseUrl = process.env.REACT_APP_ORBITVIEW_URL;
        const rotatorUrl = `${baseUrl}${p.orbitViews}`;
        setRotatorUrl(rotatorUrl);
      } else {
        console.warn("El proyecto no tiene definido orbitViews");
      }

      setUnit(null);

      window.addEventListener('message', (event) => {
        if (event.data.action === 'polygonSelected') {
          handleSelectPolygon(event.data.elementName);
        }
      });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updatePolygonColors = () => {
    const iframe = document.getElementById('project-iframe');
    if (!iframe || !iframe.contentWindow) {
      return;
    }

    const nameStatusPairs = units.map(u => {
      const unitSlugParts = u.slug?.split('-') || [];
      const typologySlugParts = u.typologySlug?.split('-') || [];
      let formattedName = '?';

      if (unitSlugParts.length >= 5 && typologySlugParts.length >= 4) {
        const block = unitSlugParts[2];
        const stage = unitSlugParts[3];
        const numero = unitSlugParts[4];
        const tipologyRaw = typologySlugParts[3];
        const tipology = normalizeTypology(tipologyRaw);
        const tower = String.fromCharCode(64 + parseInt(stage));

        formattedName = `${block}-${tower}-${tipology}-${numero}`;
      } else if (unitSlugParts.length === 4) {
        const typologyLetter = u.typologySlug.slice(-1).toUpperCase();
        formattedName = `${unitSlugParts[1]}-${unitSlugParts[2].toUpperCase()}-${typologyLetter}-${unitSlugParts[3]}`;
      } else {
        console.error("Formato inválido para unidad:", u.slug, u.typologySlug);
      }

      return { name: formattedName, status: u.status, typology: u.typologySlug };
    });

    const message = {
      action: 'toggleSvg',
      status: true,
      filtered: [],
      nameStatusPairs: nameStatusPairs,
      targetFrame: 1,
    };

    iframe.contentWindow.postMessage(message, '*');
  };

  const handleSyncStatus = async () => {
    try {
      setError(null);
      setLoading(true);
      await crmServices.syncStatus(project.id);
      const p = await getProjectById(project.id);
      setProject(p);
      setUnits([]);
      setUnit(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  const handleSyncUnits = async () => {
    try {
      setError(null);
      setLoading(true);
      await crmServices.syncUnits(project.id);
      const p = await getProjectById(project.id);
      setProject(p);
      setUnits([]);
      setUnit(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }

  const handleUnitsChange = (list) => {
    list.sort((a, b) => a.code.localeCompare(b.code));
    setUnits(list);
    setUnit(null);
  }

  const handleUnitChange = (unit) => {
    setUnit(unit);
  }

  const memoizedImageUrls = useMemo(() => {
    return projectList.reduce((acc, proj) => {
      acc[proj.id] = getMediaUrl(proj, 'image-1');
      return acc;
    }, {});
  }, [projectList]);

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div>
      {error && (
        <div>
          <div className='error-message'>
            Hubo un error: {error}
          </div>
          <br />
        </div>
      )}
      {project && (
        <div>
          <div className="header-row project-title">
            <button onClick={() => setProject(null)}>&larr;Regresar</button>
            <div className="project-name">{project.name}</div>
            <button onClick={() => handleSyncUnits()}>Sync Unidades CRM</button>
            <button onClick={() => handleSyncStatus()}>Sync Status CRM</button>
          </div>
          <div className="layout">
            <div className="left-side">
              <div className="card">
                <div className='aspect-ratio'>
                  {rotatorUrl && (
                    <iframe
                      id="project-iframe"
                      title="project"
                      loading="lazy"
                      allowFullScreen=""
                      height="100%"
                      width="100%"
                      src={rotatorUrl}
                      onLoad={() => setTimeout(updatePolygonColors, 500)}>
                    </iframe>
                  )}
                </div>
              </div>
              <div className="card">
                <ProjectQuotes project={project} units={units} unit={unit} />
              </div>
            </div>
            <div className="right-side" style={{ height: '100%' }}>
              <div className="card frame-row">
                <ProjectFilters project={project} onUnitsChange={handleUnitsChange} units={units} unit={unit} />
              </div>
              <div className="card">
                <ProjectUnits project={project} units={units} onUnitChange={handleUnitChange} />
              </div>
            </div>
          </div>
        </div>
      )}
      {!project && (
        <div>
          <h2>Seleccione proyecto</h2>
          <div className="project-list">
            {projectList.map((proj) => (
              <div key={proj.id} className="project-card" onClick={() => handleSelectProject(proj)}>
                <h3>{proj.name}</h3>
                <img src={memoizedImageUrls[proj.id]} alt={proj.name} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Salesperson;
